import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiTrash2 } from "react-icons/fi";
import { useDeleteProxyMutation } from "@RTKQuery/admin/proxy/proxy";
import { IProxy } from "@interfaces/admin/proxy";
import { proxyNumber } from "@ui";

type Modem = Pick<IProxy, "id" | "router_id" | "parent_id" | "user_id" | "modem"> & {
    children?: { id: number }[];
};

export function DeleteModemButton({ data }: { data: Modem }) {
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState("");
    const [deleteProxy, { isLoading }] = useDeleteProxyMutation();
    const cancelRef = useRef<HTMLButtonElement>(null);
    const dialogRef = useRef<HTMLDivElement>(null);
    const titleId = useId();
    const descriptionId = useId();
    const number = proxyNumber(data);
    const blockedReason = data.user_id !== null
        ? "Порт має оренду. Спочатку завершіть її в картці клієнта."
        : data.parent_id !== null || Boolean(data.children?.length)
            ? "Спочатку від’єднайте порт від групи в налаштуваннях."
            : "";

    useEffect(() => {
        if (!open) return;
        const previous = document.activeElement as HTMLElement | null;
        cancelRef.current?.focus();
        return () => { previous?.focus(); };
    }, [open]);

    useEffect(() => { if (isLoading) dialogRef.current?.focus(); }, [isLoading]);

    async function remove() {
        if (isLoading || blockedReason) return;
        setMessage("");
        try {
            await deleteProxy(data.id).unwrap();
            setOpen(false);
        } catch (error) {
            const detail = (error as { data?: { detail?: unknown } })?.data?.detail;
            setMessage(typeof detail === "string" ? detail
                : "Не вдалося видалити запис порту. Спробуйте ще раз.");
        }
    }

    return <>
        <button type="button" aria-label={`Видалити порт №${number}`}
            title="Видалити порт" className="p-1 text-[#9a5660] hover:text-[#b42318] rounded focus-visible:outline"
            onClick={() => { setMessage(""); setOpen(true); }}>
            <FiTrash2 className="w-[18px] h-[18px]" aria-hidden="true" />
        </button>
        {open && createPortal(
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
                onClick={(event) => { if (event.target === event.currentTarget && !isLoading) setOpen(false); }}>
                <div ref={dialogRef} tabIndex={-1} role="alertdialog" aria-modal="true" aria-labelledby={titleId}
                    aria-describedby={descriptionId} aria-busy={isLoading}
                    className="w-full max-w-[440px] rounded-xl bg-white p-6 shadow-xl"
                    onKeyDown={(event) => {
                        if (event.key === "Escape" && !isLoading) { event.stopPropagation(); setOpen(false); }
                        if (event.key === "Tab") {
                            const buttons = event.currentTarget.querySelectorAll<HTMLButtonElement>("button:not(:disabled)");
                            if (!buttons.length) { event.preventDefault(); return; }
                            const first = buttons[0], last = buttons[buttons.length - 1];
                            if (document.activeElement === event.currentTarget) {
                                event.preventDefault(); (event.shiftKey ? last : first)?.focus();
                            }
                            else if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
                            else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
                        }
                    }}>
                    <h2 id={titleId} className="text-lg font-semibold text-[#1b1b1d]">Видалити порт №{number}?</h2>
                    <p className="mt-2 text-sm text-[#626b7d]">{data.modem}</p>
                    <p id={descriptionId} className="mt-3 text-sm text-[#1b1b1d]">
                        {blockedReason || "Буде видалено лише запис порту з адмінки сайту. Модем і порт на ноді залишаться. Запис не повернеться після синхронізації. Історія покупок збережеться."}
                    </p>
                    {message && <p role="alert" className="mt-3 text-sm text-[#b42318]">{message}</p>}
                    <div className="mt-6 flex justify-end gap-3">
                        <button ref={cancelRef} type="button" disabled={isLoading} onClick={() => setOpen(false)}
                            className="rounded-lg border border-[#dce0e8] px-4 py-2 text-sm disabled:opacity-50">Скасувати</button>
                        <button type="button" disabled={isLoading || Boolean(blockedReason)} onClick={remove}
                            className="rounded-lg bg-[#b42318] px-4 py-2 text-sm text-white disabled:opacity-50">
                            {isLoading ? "Видалення…" : "Видалити"}
                        </button>
                    </div>
                </div>
            </div>, document.body)}
    </>;
}
